import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo/Logo';
import useFooterControl from '../../lib/useFooterControl';
import WelcomeViewButton from './components/WelcomeViewButton';

const WelcomeView = () => {
  const [contentSelector, setContentSelector] = useState('actions');
  const [paperContent, setPaperContent] = useState<React.JSX.Element>(<></>);
  const navigate = useNavigate();
  const theme = useTheme();
  const commonBoxProps: SxProps<Theme> = useMemo(
    () => ({
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1em',
      gap: '1em',
    }),
    []
  );
  useFooterControl(true, false);

  useEffect(() => {
    switch (contentSelector) {
      case 'actions':
        setPaperContent(
          <>
            <WelcomeViewButton
              action={() => setContentSelector('learn-more')}
              text={
                "I'm looking to learn more about the S3G Security Training..."
              }
            />
            <Divider flexItem orientation="vertical" />
            <WelcomeViewButton
              action={() => setContentSelector('training')}
              text={"I'm trying to begin the training..."}
            />
          </>
        );
        break;
      case 'learn-more':
        setPaperContent(
          <Box sx={commonBoxProps}>
            <Typography variant="h5" fontWeight={600}>
              Can&apos;t wait to hear from you!
            </Typography>
            <Typography>
              Thank you for your interest in our training. We&apos;re excited to
              partner with your organization to reach your training goals.
              Please contact us to learn more or receive a free demo of our
              training.
            </Typography>
            <Box display="flex" gap="1em">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setContentSelector('actions')}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate('/contact')}
                endIcon={<SendRoundedIcon />}
              >
                Contact Us
              </Button>
            </Box>
          </Box>
        );
        break;
      case 'training':
        setPaperContent(
          <Box sx={commonBoxProps}>
            <Typography variant="h5" fontWeight={600}>
              Pending access
            </Typography>
            <Typography>
              It seems that your organization hasn&apos;t added you to the
              training yet, but don’t worry—this can easily be resolved. Please
              contact your organization&apos;s administrator and ask them to add
              your email to the training.
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setContentSelector('actions')}
            >
              Back
            </Button>
          </Box>
        );
        break;
      default:
        break;
    }
  }, [commonBoxProps, contentSelector, navigate]);

  return (
    <Container maxWidth="md" sx={{ paddingTop: '2em', overflow: 'auto' }}>
      <Box display="flex" justifyContent="space-between" marginBottom="2em">
        <Typography variant="h2" fontWeight="400">
          Welcome to S3G Security
        </Typography>
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
            },
          }}
          alignItems="center"
        >
          <Logo sizeEm={3} />
        </Box>
      </Box>
      <Typography
        variant="h5"
        fontWeight="400"
        marginBottom="1em"
        marginTop="-0.75em"
      >
        How can we help?
      </Typography>
      <Paper
        elevation={3}
        sx={{
          borderColor: theme.palette.primary.main,
          borderWidth: '2px',
          borderStyle: 'solid',
          marginBottom: '2em',
        }}
      >
        <Box display="flex" height="15em">
          {paperContent}
        </Box>
      </Paper>
      <Box
        sx={{
          display: {
            xs: 'flex',
            sm: 'none',
          },
        }}
        justifyContent="center"
      >
        <Logo showText sizeEm={3} />
      </Box>
    </Container>
  );
};

export default WelcomeView;
